import React from 'react';
import { useNavigate } from 'react-router-dom';
import { journeyService } from '../services/journeyService';
import { formatCompactNumber, formatRelativeTime } from '../utils/format';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import Select from '../components/ui/Select';
import Dropdown from '../components/ui/Dropdown';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EventBus from '../utils/EventBus';
import styles from './Journeys.module.css';

const FILTERS = ['all', 'draft', 'active', 'sunset', 'paused', 'stopped'];
const PAGE_SIZE = 20;
const SORTS = [
  { value: 'updated_desc', label: 'Recently updated' },
  { value: 'created_desc', label: 'Newest first' },
  { value: 'created_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
];

const ENTRY_LABELS = { segment: 'Segment', event: 'Event', schedule: 'Scheduled' };

function entryLabel(config) {
  const type = config?.trigger?.type || 'segment';
  return ENTRY_LABELS[type] || type;
}

// Honest, config-derived summary — no segment name is stored in the journey
// config (only segment_id), so we describe the trigger shape rather than invent
// an audience name we can't resolve here.
function audienceSummary(config) {
  const tr = config?.trigger || {};
  const type = tr.type || 'segment';
  if (type === 'event') return 'On “' + (tr.event_name || 'event') + '”';
  if (type === 'schedule') return tr.cron_expression ? 'Cron: ' + tr.cron_expression : 'Scheduled';
  return 'Segment audience';
}

class JourneyRowSkeleton extends React.Component {
  render() {
    return (
      <tr className={styles.skRow}>
        {[180, 60, 70, 120, 40, 50, 40, 50, 90, 24].map((w, i) => (
          <td key={i}><Skeleton variant="text" width={w} height={13} /></td>
        ))}
      </tr>
    );
  }
}

class Journeys extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true, filter: 'all', search: '', sort: 'updated_desc',
      journeys: [], statsById: {}, total: 0, statusCounts: {}, page: 1,
      confirmDelete: null, confirmStop: null, acting: false,
    };
    this.load = this.load.bind(this);
    this._searchTimer = null;
  }

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  load() {
    const { filter, search, sort, page } = this.state;
    this.setState({ loading: true });
    journeyService.getAllWithMeta({
      page, limit: PAGE_SIZE,
      status: filter === 'all' ? undefined : filter,
      search: search || undefined,
      sort,
    })
      .then(({ data, meta }) => {
        this.setState({
          loading: false, journeys: data, total: meta.total,
          statusCounts: meta.statusCounts || {},
        });
        this.loadStats(data);
      })
      .catch(() => this.setState({ loading: false }));
  }

  // Per-row stats aren't part of the list payload (they're live counts from a
  // separate table) — fetch them in parallel for the visible page and merge in
  // as they arrive, so the table renders immediately and fills counts after.
  loadStats(journeys) {
    journeys.forEach((j) => {
      journeyService.stats(j.id)
        .then((stats) => this.setState((s) => ({ statsById: { ...s.statsById, [j.id]: stats } })))
        .catch(() => {});
    });
  }

  setFilter(filter) {
    this.setState({ filter, page: 1 }, this.load);
  }

  setSearch(search) {
    this.setState({ search });
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => this.setState({ page: 1 }, this.load), 300);
  }

  setSort(sort) {
    this.setState({ sort, page: 1 }, this.load);
  }

  setPage(page) {
    this.setState({ page }, this.load);
  }

  act(promise, successMessage) {
    this.setState({ acting: true });
    promise
      .then(() => {
        this.setState({ acting: false, confirmDelete: null, confirmStop: null });
        EventBus.emit('toast', { type: 'success', message: successMessage });
        this.load();
      })
      .catch((err) => {
        this.setState({ acting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Action failed.' });
      });
  }

  menuItems(j) {
    const items = [];
    items.push({ label: 'Open builder', onClick: () => this.props.open(j.id) });
    items.push({
      label: 'Duplicate',
      onClick: () => this.act(
        journeyService.create({ name: j.name + ' (copy)', config: j.draft_config || j.config }),
        'Journey duplicated.'
      ),
    });
    if (j.status === 'active') {
      items.push({ label: 'Pause', onClick: () => this.act(journeyService.pause(j.id), 'Journey paused.') });
      items.push({ label: 'Sunset (stop new entries)', onClick: () => this.act(journeyService.sunset(j.id), 'Journey sunset — no new entries.') });
    }
    if (j.status === 'paused' || j.status === 'sunset') {
      items.push({ label: 'Resume', onClick: () => this.act(journeyService.activate(j.id), 'Journey resumed.') });
    }
    if (j.status !== 'stopped') {
      items.push({ divider: true }, { label: 'Stop', danger: true, onClick: () => this.setState({ confirmStop: j }) });
    }
    items.push({ label: 'Delete', danger: true, onClick: () => this.setState({ confirmDelete: j }) });
    return items;
  }

  render() {
    const { loading, filter, search, sort, journeys, statsById, total, statusCounts, page, confirmDelete, confirmStop, acting } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const allCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>Journeys</div>
            <div className={styles.pageSub}>Multi-step automated lifecycle flows</div>
          </div>
          <button className={styles.newBtn} onClick={() => this.props.open('new')}>+ New Journey</button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <span className={['material-symbols-rounded', styles.searchIco].join(' ')}>search</span>
            <input
              className={styles.search}
              placeholder="Search journeys…"
              value={search}
              onChange={(e) => this.setSearch(e.target.value)}
            />
          </div>
          <Select
            size="sm"
            style={{ width: 170 }}
            value={sort}
            options={SORTS}
            onChange={(val) => this.setSort(val)}
          />
        </div>

        <div className={styles.tabs}>
          {FILTERS.map((f) => (
            <button
              key={f}
              className={[styles.tab, filter === f ? styles.tabOn : ''].join(' ')}
              onClick={() => this.setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span className={styles.tabCount}>{f === 'all' ? allCount : (statusCounts[f] || 0)}</span>
            </button>
          ))}
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Journey</th>
                <th className={styles.th}>Status</th>
                <th className={styles.th}>Entry</th>
                <th className={styles.th}>Audience / Schedule</th>
                <th className={styles.th}>Active</th>
                <th className={styles.th}>Completed</th>
                <th className={styles.th} title="Goal-conversion analytics arrive with per-node reporting">Goal</th>
                <th className={styles.th}>Link Clicks</th>
                <th className={styles.th}>Updated</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [0, 1, 2, 3, 4].map((i) => <JourneyRowSkeleton key={i} />)
                : journeys.length === 0
                  ? (
                    <tr>
                      <td colSpan={10}>
                        <EmptyState
                          icon="route"
                          title="No journeys"
                          description={filter === 'all' && !search
                            ? 'Create your first automated journey.'
                            : 'No journeys match this filter or search.'}
                        />
                      </td>
                    </tr>
                  )
                  : journeys.map((j) => {
                      const stats = statsById[j.id];
                      return (
                        <tr key={j.id} className={styles.row}>
                          <td className={styles.tdName}>
                            <span
                              className={styles.nameLink}
                              onClick={() => this.props.open(j.id)}
                              role="button"
                              tabIndex={0}
                            >
                              {j.name}
                            </span>
                            <div className={styles.tdSub}>{(j.config?.steps?.length || 0)} steps</div>
                          </td>
                          <td className={styles.td}><Badge status={j.status} dot>{j.status}</Badge></td>
                          <td className={styles.td}>{entryLabel(j.config)}</td>
                          <td className={styles.td}>{audienceSummary(j.config)}</td>
                          <td className={styles.tdNum}>{stats ? formatCompactNumber(stats.users_in_journey) : '·'}</td>
                          <td className={styles.tdNum}>{stats ? formatCompactNumber(stats.completed) : '·'}</td>
                          <td className={styles.tdNum} title="Not tracked separately yet">n/a</td>
                          <td className={styles.tdNum}>{stats ? formatCompactNumber(stats.link_clicks) : '·'}</td>
                          <td className={styles.td}>{formatRelativeTime(j.modified_on)}</td>
                          <td className={styles.tdAct}>
                            <Dropdown
                              align="right"
                              trigger={<button className={styles.actBtn}>⋯</button>}
                              items={this.menuItems(j)}
                            />
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>

        {!loading && journeys.length > 0 && (
          <div className={styles.pager}>
            <button className={styles.pagerBtn} disabled={page <= 1} onClick={() => this.setPage(page - 1)}>‹ Prev</button>
            <span className={styles.pagerInfo}>Page {page} of {totalPages} · {formatCompactNumber(total)} journeys</span>
            <button className={styles.pagerBtn} disabled={page >= totalPages} onClick={() => this.setPage(page + 1)}>Next ›</button>
          </div>
        )}

        <ConfirmDialog
          open={!!confirmDelete}
          title="Delete journey?"
          message={confirmDelete ? `"${confirmDelete.name}" will be removed. This can't be undone.` : ''}
          confirmLabel="Delete"
          loading={acting}
          onConfirm={() => this.act(journeyService.remove(confirmDelete.id), 'Journey deleted.')}
          onCancel={() => this.setState({ confirmDelete: null })}
        />

        <ConfirmDialog
          open={!!confirmStop}
          title="Stop journey?"
          message={confirmStop ? `"${confirmStop.name}" will stop enrolling and messaging users immediately. This can't be undone.` : ''}
          confirmLabel="Stop journey"
          loading={acting}
          onConfirm={() => this.act(journeyService.stop(confirmStop.id), 'Journey stopped.')}
          onCancel={() => this.setState({ confirmStop: null })}
        />
      </>
    );
  }
}

function JourneysWithNav(props) {
  const navigate = useNavigate();
  return <Journeys {...props} open={(id) => navigate('/journeys/' + id + '/builder')} />;
}

export default JourneysWithNav;
